"use client";

import { XMarkIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import {
  forwardRef,
  ForwardRefRenderFunction,
  ReactNode,
  useCallback,
  useContext,
} from "react";
import Modal from "react-modal";
import { ModalContext } from "../hooks/useModal";
import Button from "./Button";

type DialogProps = {
  onClose: () => void;
  children?: ReactNode;
  footer?: ReactNode;
  isOpen: boolean;
  className?: string;
  overlayClassName?: string;
  disabledPadding?: boolean;
  closeButton?: boolean;
};

// Content that already offers its own way out — a ConfirmAction with a
// Cancel, a shell header's back chevron — marks the element that owns it
// `data-owns-dialog-close`. The dialog's X is then a second control for the
// same action (and, in the padded layout, a dead header row stacked above the
// title), so it yields. Two selectors because the marker can sit on the
// content root itself or on something inside it; `~` rather than `+` because
// `children` may render several roots.
const CONTENT_OWNS_CLOSE = [
  "[&:has(~_[data-owns-dialog-close])]:hidden",
  "[&:has(~_*_[data-owns-dialog-close])]:hidden",
];

const DialogComponent: ForwardRefRenderFunction<Modal, DialogProps> = (
  {
    onClose,
    isOpen,
    className,
    overlayClassName,
    children,
    disabledPadding,
    closeButton,
    footer,
    ...rest
  },
  ref,
) => {
  const modal = useContext(ModalContext);
  // Stable identity on purpose: React re-invokes a ref callback whenever its
  // identity changes, so an inline `(el) => el.focus()` would re-focus this
  // wrapper on EVERY Dialog re-render — e.g. when the content flips the
  // modal's confirm/className state while the user is typing — and steal
  // focus from the field mid-edit. Memoized, it runs on mount only.
  const focusContentOnMount = useCallback((el: HTMLDivElement | null) => {
    el?.focus();
  }, []);

  return (
    <Modal
      {...rest}
      // The app router has no stable root element to pass to setAppElement
      // (content sits directly under <body>, where react-modal also portals),
      // so opt out of aria-hiding to silence the dev warning.
      ariaHideApp={false}
      isOpen={isOpen}
      overlayClassName={`${overlayClassName} bg-slate-500/50 fixed inset-0 z-[9999] flex items-center justify-center p-2 min-w-80`}
      onRequestClose={onClose}
      ref={ref}
      className={clsx(
        `mt-auto md:mt-0 relative flex flex-col overflow-hidden bg-background text-foreground outline-none max-h-full p-0 border-0 rounded-2xl backdrop:bg-slate-500/50 sm:min-w-0 sm:w-[calc(100%-theme(spacing.6))] min-w-full [&_button.flat]:!bg-surface-secondary [&_button.flat:hover]:!bg-surface-tertiary [&_button.flat[aria-expanded=true]]:!bg-surface-tertiary [&_button.tertiary:hover]:!bg-surface-secondary [&_button.tertiary[aria-expanded=true]]:!bg-surface-secondary`,
        className,
        !className?.includes("max-w-") && "max-w-md",
      )}
    >
      <div ref={modal.contentRef} className="flex-1 overflow-auto h-full">
        {/* Full-bleed dialogs (disabledPadding) own their layout and chrome
          (often full-height panels with their own headers), so the close
          button floats over their content. Padded dialogs instead reserve a
          header row in normal flow, so the button never overlaps titles. */}
        {disabledPadding && (
          <Button
            variant="secondary"
            size="m"
            onClick={onClose}
            aria-label="Close"
            className={clsx(
              "absolute top-4 left-4 z-[9999] md:top-6 md:left-6",
              !closeButton && "md:hidden",
              CONTENT_OWNS_CLOSE,
            )}
          >
            <XMarkIcon className="w-6 h-6 text-foreground-muted" />
          </Button>
        )}

        <div
          // Prevent editor event like copy/paste or undo/redo to be triggered when modal is open
          data-skip-editor-hotkeys
          tabIndex={0}
          ref={focusContentOnMount}
          className={`h-full ${
            disabledPadding ? "" : "p-4 md:p-6"
          } outline-none`}
        >
          {!disabledPadding && (
            <div
              className={clsx(
                "mb-3 md:mb-4",
                !closeButton && "md:hidden",
                CONTENT_OWNS_CLOSE,
              )}
            >
              <Button variant="secondary" size="m" onClick={onClose} aria-label="Close">
                <XMarkIcon className="w-6 h-6 text-slate-500" />
              </Button>
            </div>
          )}
          {children}
        </div>
      </div>
      {footer}
    </Modal>
  );
};

const Dialog = forwardRef(DialogComponent);

export default Dialog;
